/**
 * Gantt Chart — CSS-positioned tanker availability timeline with Zulu time axis + scrub slider
 */

import type { AARTanker, AARReceiver } from '../../../../types'
import { formatZuluTime } from '../../../../services/aarEngine'

interface GanttChartProps {
  tankers: AARTanker[]
  receivers: AARReceiver[]
  currentTime: number
  totalTime: number
  startTimeZulu: string
  isPaused: boolean
  onScrub?: (time: number) => void
}

// Color palette for receiver segments
const RECEIVER_COLORS = [
  '#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b',
  '#ec4899', '#10b981', '#f97316', '#6366f1',
]

export function GanttChart({ tankers, currentTime, totalTime, receivers, startTimeZulu, isPaused, onScrub }: GanttChartProps) {
  const pct = (t: number) => `${(t / totalTime) * 100}%`

  const receiverColorMap: Record<string, string> = {}
  receivers.forEach((r, i) => {
    receiverColorMap[r.id] = RECEIVER_COLORS[i % RECEIVER_COLORS.length]
  })

  return (
    <div className="term-box p-3">
      <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-3">Tanker Timeline</div>

      <div className="flex flex-col gap-2">
        {tankers.map(tnk => {
          const isCancelled = tnk.status === 'CANCELLED'
          return (
            <div key={tnk.id} className="flex items-center gap-3">
              {/* Label */}
              <div className="w-24 shrink-0 text-right">
                <div className={`text-xs font-mono font-bold ${isCancelled ? 'text-term-error line-through' : 'text-term-primary'}`}>
                  {tnk.callsign}
                </div>
                <div className="text-[9px] text-term-textDim">{tnk.assetType}</div>
              </div>

              {/* Timeline bar */}
              <div className="flex-1 h-8 bg-term-bgDark rounded relative overflow-hidden border border-term-border/30">
                {/* Availability window */}
                <div
                  className={`absolute top-0 bottom-0 rounded ${isCancelled ? 'bg-term-error/20' : 'bg-emerald-500/15'}`}
                  style={{
                    left: pct(tnk.availableWindow[0]),
                    width: pct(tnk.availableWindow[1] - tnk.availableWindow[0]),
                  }}
                />

                {/* Current receiver highlight */}
                {tnk.currentReceiver && (
                  <div
                    className="absolute top-1 bottom-1 rounded flex items-center justify-center"
                    style={{
                      left: pct(Math.max(0, currentTime - 3)),
                      width: pct(6),
                      backgroundColor: receiverColorMap[tnk.currentReceiver] ?? '#3b82f6',
                      opacity: 0.8,
                    }}
                  >
                    <span className="text-[8px] font-bold text-white truncate px-1">
                      {receivers.find(r => r.id === tnk.currentReceiver)?.callsign ?? ''}
                    </span>
                  </div>
                )}

                {/* Queued receivers as small markers */}
                {tnk.queuedReceivers.map((rcvId, idx) => {
                  const rcv = receivers.find(r => r.id === rcvId)
                  if (!rcv) return null
                  return (
                    <div
                      key={rcvId}
                      className="absolute top-0.5 w-1.5 h-1.5 rounded-full"
                      style={{
                        left: pct(Math.min(rcv.arctWindow[0] + idx * 2, totalTime)),
                        backgroundColor: receiverColorMap[rcvId] ?? '#6b7280',
                      }}
                      title={rcv.callsign}
                    />
                  )
                })}

                {/* Current time indicator */}
                <div
                  className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                  style={{ left: pct(currentTime) }}
                />

                {/* Offload progress fill */}
                {tnk.totalOffload > 0 && (
                  <div
                    className="absolute bottom-0 h-1 bg-term-primary/40 rounded-b"
                    style={{ left: pct(tnk.availableWindow[0]), width: `${(tnk.offloaded / tnk.totalOffload) * parseFloat(pct(tnk.availableWindow[1] - tnk.availableWindow[0]))}%` }}
                  />
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Zulu time axis */}
      <div className="flex items-center gap-3 mt-1">
        <div className="w-24 shrink-0" />
        <div className="flex-1 relative h-4">
          {Array.from({ length: Math.floor(totalTime / 30) + 1 }, (_, i) => i * 30).map(t => (
            <div
              key={t}
              className="absolute text-[9px] text-term-textDim tabular-nums"
              style={{ left: pct(t), transform: 'translateX(-50%)' }}
            >
              {formatZuluTime(startTimeZulu, t)}
            </div>
          ))}
        </div>
      </div>

      {/* Time scrub slider */}
      <div className="flex items-center gap-3 mt-2">
        <div className="w-24 shrink-0 text-right text-[9px] text-term-textDim">
          {isPaused ? 'SCRUB' : ''}
        </div>
        <input
          type="range"
          min={0}
          max={totalTime}
          value={currentTime}
          onChange={e => {
            if (isPaused && onScrub) onScrub(parseInt(e.target.value))
          }}
          disabled={!isPaused}
          className={`flex-1 h-1 accent-term-primary ${isPaused ? 'cursor-pointer opacity-100' : 'cursor-not-allowed opacity-30'}`}
        />
        <div className="text-[10px] text-term-textDim tabular-nums w-16 text-right">
          {formatZuluTime(startTimeZulu, currentTime)}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-2 px-0">
        <div className="w-24 shrink-0" />
        {receivers.map(rcv => (
          <div key={rcv.id} className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded" style={{ backgroundColor: receiverColorMap[rcv.id] }} />
            <span className="text-[9px] text-term-textDim font-mono">{rcv.callsign}</span>
          </div>
        ))}
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-0.5 bg-red-500" />
          <span className="text-[9px] text-term-textDim">Current Time</span>
        </div>
      </div>
    </div>
  )
}
